import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Beef, ChevronRight, Tag } from 'lucide-react'
import { getSmartKraalRegistrationMine } from '../../services/api'
import { BINDING_STATUS, BindingStatusChip } from '../SmartKraalBindingChip'
import { variantUrl, fallbackToOriginal } from '../../utils/media'

// The owner's animals at the crush (plan smartkraal-crush-registration, Phase 4a).
// Scoped server-side by the explicit owner-segment account link: an owner with no
// linked segment gets segments: [] and this renders nothing. Owners read the
// binding status; the assurance officer reviews (Phase 3 rule), so there are no
// verbs here, only links into the cattle list filtered by status.
const PILLS = ['sent_back', 'unbound', 'bound', 'confirmed']

// Kampala calendar day (the rest of the SmartKraal UI and the Tuesday Report count in
// Kampala days; slicing the UTC string would show the previous day after 21:00 UTC).
const KAMPALA_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Kampala', year: 'numeric', month: '2-digit', day: '2-digit' })
const fmtDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 10) : KAMPALA_DAY.format(d)
}

export default function RegistrationCard({ ranchId }) {
  const { data } = useQuery({
    queryKey: ['smartkraalRegMine', ranchId],
    queryFn: ({ signal }) => getSmartKraalRegistrationMine(ranchId, { signal }),
    enabled: !!ranchId,
    staleTime: 5 * 60 * 1000,
  })
  if (!data?.configured || !(data.segments || []).length) return null

  const counts = data.counts || {}
  const segments = data.segments
  // One segment: every pill carries it, so the list opens on this owner's animals.
  const segParam = segments.length === 1 ? `&owner_segment_id=${segments[0].id}` : ''
  const cards = data.cards || []

  return (
    <div className="card p-5" data-testid="registration-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl ring-1 bg-indigo-50 ring-indigo-100">
              <Tag className="h-5 w-5 text-indigo-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">My animals at the crush</h2>
          </div>
          <p className="text-sm text-earth-500 mt-1">
            {counts.animals} active animal{counts.animals === 1 ? '' : 's'}
            {segments.length > 1 ? ` across ${segments.map((s) => s.name).join(', ')}` : ` (${segments[0].name})`}
            {' · '}{counts.seen_30d} read by the kraal in the last 30 days
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {PILLS.map((key) => {
            const s = BINDING_STATUS[key]
            const n = counts[key] || 0
            return (
              <Link key={key} to={`/cattle?smartkraal_status=${key}${segParam}`}
                data-testid={`reg-pill-${key}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium border ${s.cls} ${n ? '' : 'opacity-60'} hover:opacity-100`}
                title={`Open the cattle list on "${s.label}"`}>
                <span className="font-bold tabular-nums">{n}</span> {s.label.toLowerCase()}
              </Link>
            )
          })}
        </div>
      </div>

      {cards.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {cards.map((a) => (
            <Link key={a.id} to={`/cattle/${a.id}`} data-testid="reg-tile"
              className="rounded-xl border border-earth-200 bg-white overflow-hidden hover:border-primary-300 hover:shadow-sm transition-colors">
              <div className="aspect-[4/3] bg-earth-50 flex items-center justify-center overflow-hidden">
                {a.photo_url ? (
                  <img src={variantUrl(a.photo_url, 'thumb')} alt={a.local_name || a.rfid_tag}
                    className="h-full w-full object-cover" loading="lazy" decoding="async"
                    onError={fallbackToOriginal(a.photo_url)} />
                ) : (
                  <Beef className="h-8 w-8 text-earth-300" />
                )}
              </div>
              <div className="p-2.5 space-y-1">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-sm font-medium text-gray-900 truncate">{a.local_name || a.rfid_tag}</span>
                  {a.p_idtag ? (
                    <span className="text-xs text-earth-500 whitespace-nowrap">SK {a.p_idtag}</span>
                  ) : (
                    <span className="text-xs text-earth-400 whitespace-nowrap">no tag</span>
                  )}
                </div>
                <BindingStatusChip status={a.status} />
                {a.status === 'sent_back' && a.review_note && (
                  <p className="text-xs text-red-700 leading-snug line-clamp-2" title={a.review_note}>
                    {a.reviewer_name ? `${a.reviewer_name}: ` : ''}{a.review_note}
                  </p>
                )}
                {a.status !== 'sent_back' && a.kraal_last_weight_kg != null && (
                  <p className="text-xs text-earth-500">
                    {Math.round(a.kraal_last_weight_kg)} kg at the crush{fmtDate(a.last_weighed_utc) ? ` · ${fmtDate(a.last_weighed_utc)}` : ''}
                  </p>
                )}
                {a.status === 'unbound' && (
                  <p className="text-xs text-earth-500">No SmartKraal tag yet</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mt-4 text-sm">
        {segments.map((s) => (
          <Link key={s.id || s.name} to={s.id ? `/cattle?owner_segment_id=${s.id}` : '/cattle'}
            className="inline-flex items-center gap-1 text-primary-700 hover:underline">
            All my animals{segments.length > 1 ? ` (${s.name})` : ''} <ChevronRight className="h-4 w-4" />
          </Link>
        ))}
        {data.data_as_of_utc && (
          <span className="text-xs text-earth-400 ml-auto">Kraal data as of {fmtDate(data.data_as_of_utc)}</span>
        )}
      </div>
    </div>
  )
}
